import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {IPageNode} from './page.model';

@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<IPageNode[]>([]);
  get data(): IPageNode[] { return this.dataChange.value; }
  private _edit = true;
  set edit(value) {
    this._edit = value;
  }
  constructor() {}

  initialize(tab) {
    tab = this.reOrder(tab);
    const data = this.buildFileTree(tab, 0);
    this.dataChange.next(data);
  }

  private reOrder(tab) {
    const tab2 = [];
    let order = null;
    let index = 0;
    for (const _t of tab) {
      const t = {..._t};
      order = (t.data.order !== 0 ? (t.data.order - 1) : index);
      index = index + 1;
      tab2[order] = {...t};
      if (t.children) {
        tab2[order].children = this.reOrder(t.children);
      }
    }
    return tab2;
  }

  private _makeNode(obj, key, value, level, node) {
    if (typeof value === 'object' && value.children !== undefined) {
      node.children = this.buildFileTree(value.children, level + 1);
    } else {
      node.item = {'id' : obj[key]['id'], 'data' : obj[key]['data']};
    }
    return node;
  }
  private _makeConsultNode(obj, key, value, level, node) {
    if (value != null && value.is_published) {
      return this._makeNode(obj, key, value, level, node);
    }
    return null;
  }
  private _makeEditNode(obj, key, value, level, node) {
    if (value != null) {
      return this._makeNode(obj, key, value, level, node);
    }
    return value;
  }
  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `IPageNode`.
   */
  buildFileTree(obj: object, level: number): IPageNode[] {
    return Object.keys(obj).reduce<IPageNode[]>((accumulator, key) => {
      const value = obj[key];
      let node = new IPageNode();
      node.item = {'id' : obj[key]['id'], 'data' : obj[key]['data']};
      node = this._edit
        ? this._makeEditNode(obj, key, value, level, node)
        : this._makeConsultNode(obj, key, value, level, node);
      if (node) {
        return accumulator.concat(node);
      }
      return accumulator;
    }, []);
  }

  /** Add an item to to-do list */
  insertItem(parent: IPageNode, name: string): IPageNode {
    if (!parent.children) {
      parent.children = [];
    }
    const newItem = { item: name } as IPageNode;
    parent.children.push(newItem);
    this.dataChange.next(this.data);
    return newItem;
  }

  insertItemAbove(node: IPageNode, name: string): IPageNode {
    const parentNode = this.getParentFromNodes(node);
    const newItem = { item: name } as IPageNode;
    if (parentNode != null) {
      parentNode.children.splice(parentNode.children.indexOf(node), 0, newItem);
    } else {
      this.data.splice(this.data.indexOf(node), 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  insertItemBelow(node: IPageNode, name: string): IPageNode {
    const parentNode = this.getParentFromNodes(node);
    const newItem = { item: name } as IPageNode;
    if (parentNode != null) {
      parentNode.children.splice(parentNode.children.indexOf(node) + 1, 0, newItem);
    } else {
      this.data.splice(this.data.indexOf(node) + 1, 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  getParentFromNodes(node: IPageNode): IPageNode {
    for (let i = 0; i < this.data.length; ++i) {
      const currentRoot = this.data[i];
      const parent = this.getParent(currentRoot, node);
      if (parent != null) {
        return parent;
      }
    }
    return null;
  }

  getParent(currentRoot: IPageNode, node: IPageNode): IPageNode {
    if (currentRoot.children && currentRoot.children.length > 0) {
      for (let i = 0; i < currentRoot.children.length; ++i) {
        const child = currentRoot.children[i];
        if (child === node) {
          return currentRoot;
        } else if (child.children && child.children.length > 0) {
          const parent = this.getParent(child, node);
          if (parent != null) {
            return parent;
          }
        }
      }
    }
    return null;
  }

  updateItem(node: IPageNode, name: string) {
    node.item = name;
    this.dataChange.next(this.data);
  }

  deleteItem(node: IPageNode) {
    this.deleteNode(this.data, node);
    this.dataChange.next(this.data);
  }

  copyPasteItem(from: IPageNode, to: IPageNode): IPageNode {
    const newItem = this.insertItem(to, from.item);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemAbove(from: IPageNode, to: IPageNode): IPageNode {
    const newItem = this.insertItemAbove(to, from.item);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemBelow(from: IPageNode, to: IPageNode): IPageNode {
    const newItem = this.insertItemBelow(to, from.item);
    if (from.children) {
      from.children.forEach(child => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  deleteNode(nodes: IPageNode[], nodeToDelete: IPageNode) {
    const index = nodes.indexOf(nodeToDelete, 0);
    if (index > -1) {
      nodes.splice(index, 1);
    } else {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          this.deleteNode(node.children, nodeToDelete);
        }
      });
    }
  }
}
